import React, { useState } from "react";

// The client's answer on the public quote page (/quote/<ref>?k=<key>).
// respond comes from /api/quote (see api/_lib/quoteRespond.js):
//   mode "open"       - Accept quote / Request changes
//   mode "accepted"   - thanks + next steps
//   mode "superseded" - use the newer version
//   mode "closed"     - no longer open
//   mode "nokey"      - an old link without its private key, or the team's
//                       own "View Quote": no buttons (the team sees a note).
// Light, plain styling: this sits on the client's document, not the app.

const BRAND = "#3a52cc";
const INK = "#0e1520";
const MUTED = "#5a6a7a";

const fmtGBP = n => "£" + Number(n).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// "Mon 16 Nov 2026" (no comma after the day, as in the app).
const dayLabel = iso => { if (!iso) return ""; const p = Object.fromEntries(new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric", timeZone: "Europe/London" }).formatToParts(new Date(iso + "T12:00:00Z")).map(x => [x.type, x.value])); return `${p.weekday} ${p.day} ${p.month} ${p.year}`; };
const datesLabel = j => j?.start ? (j.end && j.end !== j.start ? `${dayLabel(j.start)} to ${dayLabel(j.end)}` : dayLabel(j.start)) : "";

function NextSteps({ respond }) {
  const dates = datesLabel(respond.job);
  return (
    <span>
      {respond.automotive ? "We’ll send your deposit invoice shortly; paying it secures your booking. " : "We’ll send your service contract shortly. "}
      {dates ? <>Provisional date: <strong>{dates}</strong>. We’ll confirm it with you when we send your {respond.automotive ? "deposit invoice" : "contract"}.</> : "We’ll be in touch shortly to arrange a suitable date."}
    </span>
  );
}

const btn = (primary) => ({
  background: primary ? BRAND : "#fff", color: primary ? "#fff" : BRAND, border: `1.5px solid ${BRAND}`,
  padding: "11px 20px", borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "'IBM Plex Sans', Helvetica, Arial, sans-serif",
  minHeight: 44,
});

function Modal({ title, onClose, children }) {
  return (
    <div role="dialog" aria-modal="true" aria-label={title} onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(14,21,32,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 1000 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ fontFamily: "'IBM Plex Sans', Helvetica, Arial, sans-serif", lineHeight: 1.5, background: "#fff", borderRadius: 12, padding: "22px 22px 18px", width: "100%", maxWidth: 460, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 12px 40px rgba(0,0,0,0.25)", color: INK }}>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 12 }}>{title}</div>
        {children}
      </div>
    </div>
  );
}

export default function QuoteRespondBar({ quoteId, linkKey, respond, onRespond, totalExVat, crmUser }) {
  const [open, setOpen] = useState(null); // "accept" | "changes" | null
  const [agreed, setAgreed] = useState(false);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [changesSent, setChangesSent] = useState(false);

  if (!respond) return null;
  const { mode, automotive, contact } = respond;
  const incVat = Number(String(totalExVat || "").replace(/[£,\s]/g, "")) * 1.2;

  async function submit(action) {
    setBusy(true); setError(null);
    try {
      const res = await fetch("/api/quote-respond", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ref: quoteId, k: linkKey, action, message, termsAgreed: agreed }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.respond) onRespond(data.respond);
      if (!res.ok) { setError(data.error || "Something went wrong. Please reply to our email instead."); return; }
      setOpen(null);
      if (action === "changes") { setChangesSent(true); setMessage(""); }
    } catch (e) {
      setError("We couldn’t reach our system. Please check your connection, or reply to our email.");
    } finally {
      setBusy(false);
    }
  }

  const bar = (children, tone = "#f4f6fb") => (
    <div style={{ flex: "none", background: tone, borderBottom: "1px solid #dde2ea", padding: "12px 16px", fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 14, color: INK, lineHeight: 1.5 }}>
      <div style={{ maxWidth: 794, margin: "0 auto" }}>{children}</div>
    </div>
  );

  if (mode === "nokey") {
    if (crmUser) return bar(<span style={{ color: MUTED }}>Team view. Clients see <strong>Accept quote</strong> and <strong>Request changes</strong> on the link in their quote email.</span>);
    if (!respond.awaiting) return null;
    return bar(<span>To accept this quote, just reply to our email or call or WhatsApp us on <a href={`tel:${contact.phone.replace(/\s/g, "")}`} style={{ color: BRAND, fontWeight: 600 }}>{contact.phone}</a>.</span>);
  }
  if (mode === "superseded") return bar(<span>This quote has been updated. Please use the link in our latest email, or call us on {contact.phone}.</span>, "#fff8e6");
  if (mode === "closed") return bar(<span>This quote is no longer open. To pick it up again, please call us on {contact.phone} or email <a href={`mailto:${contact.email}`} style={{ color: BRAND }}>{contact.email}</a>.</span>, "#fff8e6");
  if (mode === "accepted") return bar(<span><strong style={{ color: "#1b7a43" }}>✓ You’ve accepted this quote. Thank you.</strong> <NextSteps respond={respond} /></span>, "#eaf7ef");

  // mode === "open"
  return (
    <>
      {bar(
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <span>
            <span style={{ fontWeight: 600 }}>{changesSent ? "Thanks, we’ve got your message and will be in touch. You can still accept the quote as it is." : "Happy with this quote?"}</span>
            {automotive && <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: MUTED, fontSize: 13, marginLeft: 10, whiteSpace: "nowrap" }}>Terms of Service</a>}
          </span>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", flex: "1 1 260px", justifyContent: "flex-end" }}>
            <button style={{ ...btn(false), flex: "1 1 120px", maxWidth: 200 }} onClick={() => { setError(null); setOpen("changes"); }}>Request changes</button>
            <button style={{ ...btn(true), flex: "1 1 120px", maxWidth: 200 }} onClick={() => { setError(null); setOpen("accept"); }}>Accept quote</button>
          </div>
        </div>
      )}

      {open === "accept" && (
        <Modal title={`Accept quote ${quoteId}`} onClose={() => !busy && setOpen(null)}>
          {incVat > 0 && <p style={{ margin: "0 0 10px", fontSize: 15 }}>Total: <strong>{fmtGBP(incVat)}</strong> inc VAT</p>}
          <p style={{ margin: "0 0 14px", fontSize: 14, color: MUTED }}><NextSteps respond={respond} /></p>
          {automotive && (
            <div style={{ background: "#f4f6fb", borderRadius: 8, padding: "10px 14px", margin: "0 0 14px", fontSize: 13.5 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Key terms</div>
              <ul style={{ margin: 0, paddingLeft: 18, color: INK }}>
                <li>The 50% deposit{incVat > 0 ? ` (${fmtGBP(incVat / 2)} inc VAT)` : ""} secures your booking; the balance is due on {respond.inUnit === false ? "completion" : "collection"}.</li>
                <li>Cancel within 14 days of accepting for a full refund, if no work has started.</li>
                <li>Later cancellations: full refund or a free date change with 7+ days’ notice; part of the deposit is kept with shorter notice.</li>
              </ul>
              <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: BRAND, fontWeight: 600, display: "inline-block", marginTop: 6 }}>Read the full Terms of Service</a>
            </div>
          )}
          {automotive && (
            <label style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 14, margin: "0 0 16px", cursor: "pointer" }}>
              <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} style={{ width: 20, height: 20, marginTop: 1, flex: "none" }} />
              <span>I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: BRAND, fontWeight: 600 }}>Terms of Service</a>, including the 14-day cancellation terms.</span>
            </label>
          )}
          {error && <p style={{ color: "#c62828", fontSize: 14, margin: "0 0 12px" }}>{error}</p>}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
            <button style={btn(false)} disabled={busy} onClick={() => setOpen(null)}>Cancel</button>
            <button style={{ ...btn(true), opacity: busy || (automotive && !agreed) ? 0.5 : 1 }} disabled={busy || (automotive && !agreed)} onClick={() => submit("accept")}>{busy ? "Accepting…" : "Confirm and accept"}</button>
          </div>
        </Modal>
      )}

      {open === "changes" && (
        <Modal title="Request changes" onClose={() => !busy && setOpen(null)}>
          <p style={{ margin: "0 0 10px", fontSize: 14, color: MUTED }}>Tell us what you’d like changed and we’ll come back to you with an updated quote.</p>
          <textarea value={message} onChange={e => setMessage(e.target.value)} maxLength={2000} rows={5} autoFocus
            style={{ width: "100%", boxSizing: "border-box", fontFamily: "inherit", fontSize: 15, padding: 10, border: "1px solid #c5ccd8", borderRadius: 8, resize: "vertical", marginBottom: 12 }}
            placeholder="For example: could you add the wheel arches, or move it to the following week?" />
          {error && <p style={{ color: "#c62828", fontSize: 14, margin: "0 0 12px" }}>{error}</p>}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
            <button style={btn(false)} disabled={busy} onClick={() => setOpen(null)}>Cancel</button>
            <button style={{ ...btn(true), opacity: busy || !message.trim() ? 0.5 : 1 }} disabled={busy || !message.trim()} onClick={() => submit("changes")}>{busy ? "Sending…" : "Send"}</button>
          </div>
        </Modal>
      )}
    </>
  );
}
